import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Heading,
  Loading,
  Row,
  Screen,
  TextField,
  Title,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import './admin.css';

/**
 * Admin (§7). Phase 2 covers cart registration — creating a cart, issuing and
 * rotating its setup code, revoking it — plus the audit trail §0.6 requires to
 * be admin-visible. Call lists, per-staff counts and settings come in Phase 7.
 */

interface Cart {
  id: string;
  name: string;
  location_label: string | null;
  active: boolean;
  has_token: boolean;
}

interface AuditRow {
  id: string;
  at: string;
  actor_type: string;
  actor_label: string | null;
  action: string;
  cart_id: string | null;
  ip: string | null;
}

export default function Admin() {
  const [carts, setCarts] = useState<Cart[] | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<{ ok: boolean; checks: Record<string, { ok: boolean; detail: string }> } | null>(
    null
  );

  /** Shown once, right after it is issued — the server never returns it again. */
  const [freshCode, setFreshCode] = useState<{ cartName: string; code: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, a, h] = await Promise.all([
        api<{ carts: Cart[] }>('/api/admin/carts'),
        api<{ entries: AuditRow[] }>('/api/admin/audit?limit=25'),
        api<{ ok: boolean; checks: Record<string, { ok: boolean; detail: string }> }>('/api/admin/health'),
      ]);
      setCarts(c.carts);
      setAudit(a.entries);
      setHealth(h);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the admin screen.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  return (
    <Screen width="wide">
      <Title sub="Admin">Mulaqaat</Title>

      <Row className="admin__back">
        <Link to="/" className="btn btn--ghost">
          ← Back
        </Link>
      </Row>

      {error ? (
        <Card>
          <ErrorState message={error} onRetry={() => void load()} />
        </Card>
      ) : null}

      {freshCode ? (
        <Card className="admin__codecard">
          <Heading>Setup code for {freshCode.cartName}</Heading>
          <p className="admin__lead">
            Type this into that cart&apos;s screen once. It is shown now and never again — only its hash is
            stored. If it is lost, issue a new one.
          </p>
          <code className="admin__code">{freshCode.code}</code>
          <Row>
            <Button variant="secondary" onClick={() => setFreshCode(null)}>
              Done
            </Button>
          </Row>
        </Card>
      ) : null}

      <NewCart
        onCreated={(cartName, code) => {
          setFreshCode({ cartName, code });
          void load();
        }}
      />

      <Card>
        <Heading>Carts</Heading>
        {!carts ? (
          <Loading label="Loading carts…" />
        ) : carts.length === 0 ? (
          <EmptyState line="No carts yet. Add the first one above." />
        ) : (
          <ul className="admin__list">
            {carts.map((c) => (
              <CartRow
                key={c.id}
                cart={c}
                onCode={(code) => setFreshCode({ cartName: c.name, code })}
                onChanged={() => void load()}
              />
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <Heading>System</Heading>
        {!health ? (
          <Loading label="Checking…" />
        ) : (
          <ul className="admin__checks">
            {Object.entries(health.checks).map(([k, v]) => (
              <li key={k} className="admin__check">
                <span className={`admin__pip ${v.ok ? 'admin__pip--ok' : 'admin__pip--bad'}`} />
                <span className="admin__check-name">{k.replace(/_/g, ' ')}</span>
                <span className="admin__check-detail">{v.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <Heading>Recent activity</Heading>
        {!audit ? (
          <Loading label="Loading activity…" />
        ) : audit.length === 0 ? (
          <EmptyState line="Nothing recorded yet." />
        ) : (
          <ul className="admin__audit">
            {audit.map((a) => (
              <li key={a.id} className="admin__auditrow">
                <span className="admin__auditaction">{a.action}</span>
                <span className="admin__auditwho">{a.actor_label ?? a.actor_type}</span>
                <time className="admin__auditat" dateTime={a.at}>
                  {new Date(a.at).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Screen>
  );
}

function NewCart({ onCreated }: { onCreated: (cartName: string, code: string) => void }) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ setup_code: string; cart: { name: string } }>('/api/admin/carts', {
        method: 'POST',
        body: { name, location_label: location || null },
      });
      setName('');
      setLocation('');
      onCreated(res.cart.name, res.setup_code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that cart.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Heading>Add a cart</Heading>
      <form onSubmit={submit} noValidate>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Cart 2"
          maxLength={60}
        />
        <TextField
          label="Where it stands (optional)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Upstairs showroom"
          maxLength={120}
        />
        {error ? (
          <p className="field__error" role="alert" style={{ marginBottom: 14 }}>
            {error}
          </p>
        ) : null}
        <Button type="submit" busy={busy} disabled={name.trim().length === 0}>
          {busy ? 'Adding…' : 'Add cart'}
        </Button>
      </form>
    </Card>
  );
}

function CartRow({
  cart,
  onCode,
  onChanged,
}: {
  cart: Cart;
  onCode: (code: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<'rotate' | 'revoke' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function rotate() {
    if (busy) return;
    setBusy('rotate');
    setError(null);
    try {
      const res = await api<{ setup_code: string }>(`/api/admin/carts/${cart.id}/rotate`, { method: 'POST' });
      onCode(res.setup_code);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not issue a new code.');
    } finally {
      setBusy(null);
    }
  }

  async function revoke() {
    if (busy) return;
    setBusy('revoke');
    setError(null);
    try {
      await api(`/api/admin/carts/${cart.id}/revoke`, { method: 'POST' });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke that cart.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className="admin__item">
      <div className="admin__itemhead">
        <strong>{cart.name}</strong>
        {cart.active ? <Chip tone="live">Active</Chip> : <Chip tone="urgent">Revoked</Chip>}
        {cart.has_token ? null : <Chip tone="neutral">No code yet</Chip>}
      </div>
      {cart.location_label ? <p className="admin__itemsub">{cart.location_label}</p> : null}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}
      <Row>
        <Button variant="secondary" onClick={() => void rotate()} busy={busy === 'rotate'} disabled={!!busy}>
          New setup code
        </Button>
        {cart.active ? (
          <Button variant="ghost" onClick={() => void revoke()} busy={busy === 'revoke'} disabled={!!busy}>
            Revoke
          </Button>
        ) : null}
      </Row>
    </li>
  );
}
